import { useState, useRef, useEffect, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, User, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ContactOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string;
  email: string | null;
}

export interface ContactSelectComboboxProps {
  contacts: ContactOption[];
  value: string | null;
  onChange: (contactId: string | null) => void;
  onSearchChange?: (query: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export function ContactSelectCombobox({
  contacts,
  value,
  onChange,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  placeholder = "Search contacts...",
  label,
  disabled = false,
  className,
}: ContactSelectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  // Keep selectedContact in sync with value — find from current contacts list or clear.
  useEffect(() => {
    if (value === null) {
      setSelectedContact(null);
      return;
    }
    const found = contacts.find((c) => c.id === value);
    if (found) setSelectedContact(found);
    // If not found in current list, keep the previously stored selectedContact.
  }, [value, contacts]);

  const selected = value !== null ? selectedContact : null;

  const filtered = useMemo(() => {
    if (onSearchChange) return contacts;
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.display_name.toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q) ||
        [c.first_name, c.last_name].filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [contacts, query, onSearchChange]);

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    onSearchChange?.(newQuery);
  };

  const handleSelect = (contactId: string) => {
    onChange(contactId);
    handleQueryChange("");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    handleQueryChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      handleQueryChange("");
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreRef.current || isLoadingRef.current) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      onLoadMoreRef.current?.();
    }
  };

  useEffect(() => {
    if (open) {
      setQuery("");
      inputRef.current?.focus();
    }
  }, [open]);

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      {selected && !open ? (
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <User className="size-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">
            {selected.display_name}
            {selected.email && (
              <span className="ml-1 text-muted-foreground">({selected.email})</span>
            )}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear selection"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <div className="relative flex w-full">
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                className="pr-8"
              />
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 shrink-0 opacity-50" />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div
              className="max-h-[240px] overflow-y-auto overscroll-contain p-1"
              onScroll={handleScroll}
            >
              {isLoading && contacts.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  {contacts.length === 0 ? "No contacts available" : "No matching contacts"}
                </div>
              ) : (
                <>
                  {filtered.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={cn(
                        "flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                        "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none",
                      )}
                      onClick={() => handleSelect(c.id)}
                    >
                      <User className="size-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate text-left">
                        {c.display_name}
                        {c.email && (
                          <span className="ml-1 text-xs text-muted-foreground">({c.email})</span>
                        )}
                      </span>
                    </button>
                  ))}
                  {isLoading && (
                    <div className="px-2 py-2 text-center text-sm text-muted-foreground">
                      Loading more...
                    </div>
                  )}
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
